/* eslint-disable no-param-reassign */
/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */

import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import { datadogRum } from "@datadog/browser-rum";
import styled from "styled-components";

import * as API from "../../../../api/billing";
import Modal from "../../../reusable/reactModal";
import { CancelModalPropTypes } from "../lib/types";
import {
  StyledButton,
  StyledButtonContainer,
  StyledModalBody,
  StyledModalContents,
  StyledTextarea,
  StyledSelect,
} from "../lib/styles";
import { cancellationReasons, cancellationCompetitors } from "../lib/constants";
import { handleErrorsArray } from "../../../../utils";
import { redesignTheme } from "../../../theme/theme";

const StyledParagraph = styled.p`
  font-size: ${redesignTheme.fontSize[100]};
  margin-bottom: ${redesignTheme.spacing[4]};
`;

const StyledBoldParagraph = styled(StyledParagraph)`
  font-weight: ${redesignTheme.fontWeight.semibold};
`;

const StyledHeader = styled.h3`
  margin-top: ${redesignTheme.spacing[4.5]};
  margin-bottom: 0;
  font-size: ${redesignTheme.fontSize[50]};
  font-weight: ${redesignTheme.fontWeight.medium};
  font-family: ${redesignTheme.fontFamily.interface};
  color: ${redesignTheme.color.gray900};
`;

const StyledOptional = styled.span`
  color: ${redesignTheme.color.gray500};
`;

const StyledHeaderMarginBottom = styled(StyledHeader)`
  margin-top: ${redesignTheme.spacing[4.5]};
  margin-bottom: ${redesignTheme.spacing[4.5]};
`;

const StyledDescription = styled.p`
  margin-top: ${redesignTheme.spacing[1.5]};
  margin-bottom: ${redesignTheme.spacing[4.5]};
  font-size: ${redesignTheme.fontSize[50]};
  font-weight: ${redesignTheme.fontWeight.normal};
  color: ${redesignTheme.color.gray600};
`;

const StyledLabel = styled.label`
  margin-left: ${redesignTheme.spacing[2.5]};
  margin-bottom: ${redesignTheme.spacing[3.5]};
  font-family: ${redesignTheme.fontFamily.interface};
  font-size: ${redesignTheme.fontSize[50]};
`;

const StyledLink = styled.a`
  color: ${redesignTheme.color.blue500};
  text-decoration: none;
  font-size: ${redesignTheme.fontSize[50]};
  font-weight: ${redesignTheme.fontWeight.medium};
  &:hover {
    color: ${redesignTheme.color.blue700};
    text-decoration: none;
  }
`;

const CancelModal = ({
  closeModal,
  modal,
  updateToastMessage,
}: CancelModalPropTypes): React.ReactElement => {
  const [reasons, setReasons] = React.useState(cancellationReasons);
  const [competitor, setCompetitor] = React.useState("");
  const [userInput, setUserInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const checkedReason = reasons.find((reason) => {
    return reason.checked;
  });

  const formIsValid = (): boolean => {
    if (!checkedReason || !userInput) return false;
    return true;
  };

  const handleCheckboxChange = (event): void => {
    const updatedReasons = cancellationReasons.map((reason) => {
      if (reason.text === event.target.name) {
        reason.checked = !reason.checked;
      } else {
        reason.checked = false;
      }
      return reason;
    });

    setReasons(updatedReasons);
  };

  const handleTextareaChange = (event): void => {
    setUserInput(event.target.value);
  };

  const cancelAccount = (): void => {
    const postCancellation = async (): Promise<void> => {
      try {
        setLoading(true);
        const data = {
          cancel_form: {
            competitor: competitor,
            reasons: [checkedReason.text],
            user_input: userInput,
          },
        };
        await API.postCancelAccount(data);

        setLoading(false);
        closeModal();
        updateToastMessage({
          isOpen: true,
          message: i18n.translate(
            "admin_pages.settings.subscription.subscription_cancelled"
          ),
          type: "success",
        });
      } catch (error) {
        const errors = handleErrorsArray(error);
        datadogRum.addError(errors);
        setLoading(false);
        updateToastMessage({
          isOpen: true,
          message: i18n.translate(
            "admin_pages.settings.subscription.subscription_cancelled_error"
          ),
          type: "error",
        });
      }
    };

    postCancellation();
  };

  return (
    <Modal
      ariaLabel="cancel modal"
      close={closeModal}
      showDialog={modal === "cancel"}
      testId="cancel-modal"
      title={`${i18n.translate(
        "admin_pages.settings.billing.cancel_modal.title"
      )} 😢`}
    >
      <StyledModalBody data-testid="cancel-modal-body">
        <StyledModalContents>
          <StyledBoldParagraph>
            {i18n.translate(
              "admin_pages.settings.billing.cancel_modal.paragraph_1"
            )}
          </StyledBoldParagraph>
          <StyledParagraph>
            {i18n.translate(
              "admin_pages.settings.billing.cancel_modal.paragraph_2"
            )}
          </StyledParagraph>
          <div>
            <StyledLink href="/company/accounts">
              {i18n.translate(
                "admin_pages.settings.billing.cancel_modal.paragraph_3"
              )}
            </StyledLink>
          </div>
          <StyledHeader>
            {i18n.translate(
              "admin_pages.settings.billing.cancel_modal.header_1"
            )}
          </StyledHeader>
          <StyledDescription>
            {i18n.translate(
              "admin_pages.settings.billing.cancel_modal.subheader_1"
            )}
          </StyledDescription>
          {reasons.map((reason) => {
            return (
              <div key={reason.id}>
                <input
                  checked={reason.checked}
                  data-testid={reason.id}
                  id={reason.id}
                  name={reason.text}
                  onChange={handleCheckboxChange}
                  style={{
                    marginTop: "0px",
                    marginBottom: "2px",
                    verticalAlign: "middle",
                  }}
                  type="checkbox"
                />
                <StyledLabel htmlFor={reason.id}>{reason.text}</StyledLabel>
              </div>
            );
          })}

          <StyledHeader
            style={{
              marginBottom: redesignTheme.spacing[4.5],
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {i18n.translate(
              "admin_pages.settings.billing.cancel_modal.header_2"
            )}
            <StyledOptional>
              {i18n.translate(
                "admin_pages.settings.billing.cancel_modal.optional"
              )}
            </StyledOptional>
          </StyledHeader>
          <StyledSelect
            data-testid="competitor-select"
            id="competitor"
            onChange={(event): void => {
              return setCompetitor(event.target.value);
            }}
            value={competitor}
          >
            <option value="">Select a competitor</option>
            {cancellationCompetitors.map((competitor) => {
              return (
                <option value={competitor} key={competitor}>
                  {competitor}
                </option>
              );
            })}
          </StyledSelect>

          <>
            <StyledHeaderMarginBottom>
              {i18n.translate(
                "admin_pages.settings.billing.cancel_modal.cancellation_reason_text_box"
              )}
            </StyledHeaderMarginBottom>
            <StyledTextarea
              data-testid="user-input-textarea"
              onChange={handleTextareaChange}
              value={userInput}
            />
          </>
        </StyledModalContents>
        <StyledButtonContainer>
          <StyledButton
            disabled={loading}
            onClick={closeModal}
            size="small"
            testId="not-ready-button"
            variant="secondary"
          >
            {i18n.translate(
              "admin_pages.settings.billing.cancel_modal.not_ready"
            )}
          </StyledButton>
          <StyledButton
            disabled={loading || !formIsValid()}
            onClick={cancelAccount}
            size="small"
            testId="cancel-account-button"
            variant="primary"
          >
            {i18n.translate(
              "admin_pages.settings.billing.cancel_modal.cancel_account"
            )}
          </StyledButton>
        </StyledButtonContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default CancelModal;
